import React, { useEffect, useState, useRef } from 'react';

const Hero = () => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const titleRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [staticOffset, setStaticOffset] = useState({ x: 0, y: 0 });
  
  // Easter Egg State
  const [clickCount, setClickCount] = useState(0);
  const [easterEggMessage, setEasterEggMessage] = useState(null);
  const clickTimeoutRef = useRef(null);

  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Check for birthday
  const checkBirthday = () => {
    const today = new Date();
    const month = today.getMonth() + 1; // 1-12
    const day = today.getDate();
    const year = today.getFullYear();
    
    // November 10 is RHY's birthday
    if (month === 11 && day === 10) {
      return { isBirthday: true, year };
    }
    return { isBirthday: false, year };
  };

  // Handle RHY title clicks for Easter Egg
  const handleTitleClick = () => {
    const newCount = clickCount + 1;
    setClickCount(newCount);
    
    // Reset click count after 3 seconds of inactivity
    if (clickTimeoutRef.current) clearTimeout(clickTimeoutRef.current);
    clickTimeoutRef.current = setTimeout(() => setClickCount(0), 3000);
    
    const { isBirthday, year } = checkBirthday();
    
    // Show messages based on click count
    if (newCount === 3) {
      setEasterEggMessage({ text: '11', type: 'number' });
      setTimeout(() => setEasterEggMessage(null), 2000);
    } else if (newCount === 6) {
      setEasterEggMessage({ text: '10', type: 'number' });
      setTimeout(() => setEasterEggMessage(null), 2000);
    } else if (newCount === 9) {
      if (isBirthday) {
        setEasterEggMessage({ text: 'HAPPY BIRTHDAY RHY', type: 'birthday' });
        setTimeout(() => {
          setEasterEggMessage(null);
          setClickCount(0); // Reset for next year
        }, 5000);
      } else {
        // Always show 2026
        setEasterEggMessage({ text: '2026', type: 'number' });
        setTimeout(() => {
          setEasterEggMessage(null);
          setClickCount(0);
        }, 2000);
      }
    }
  };
  // Smooth TV static effect
  useEffect(() => {
    if (!isHovered) return;
    
    let animationId;
    let time = 0;
    
    const animateStatic = () => {
      time += 0.05;
      
      // Smooth sine-based movement for TV static feel
      const staticX = Math.sin(time * 3) * 2 + Math.sin(time * 7) * 0.5;
      const staticY = Math.cos(time * 2) * 1 + Math.cos(time * 5) * 0.3;
      
      setStaticOffset({ x: staticX, y: staticY });
      animationId = requestAnimationFrame(animateStatic);
    };
    
    animateStatic();
    
    return () => {
      if (animationId) cancelAnimationFrame(animationId);
    };
  }, [isHovered]);

  const handleMouseMove = (e) => {
    if (!titleRef.current) return;
    const rect = titleRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width - 0.5) * 2;
    const y = ((e.clientY - rect.top) / rect.height - 0.5) * 2;
    setMousePos({ x, y });
  };

  return (
    <section className="hero-section" data-testid="hero-section">
      {/* Pure Black Background */}
      <div className="hero-background">
        <div className="absolute inset-0 bg-[#000000]" />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-[#050505] z-10" />
      </div>

      {/* Hero Content */}
      <div className="hero-content">
        {/* Interactive RHY Title */}
        <div
          ref={titleRef}
          className="relative cursor-pointer select-none"
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => { setIsHovered(false); setMousePos({ x: 0, y: 0 }); setStaticOffset({ x: 0, y: 0 }); }}
          onMouseMove={handleMouseMove}
          onClick={handleTitleClick}
        >
          <h1 
            className={`hero-title relative ${isLoaded ? 'animate-fade-in-up' : 'opacity-0'}`}
            data-testid="hero-title"
            style={{
              transform: isHovered 
                ? `perspective(1000px) rotateX(${mousePos.y * -6}deg) rotateY(${mousePos.x * 6}deg) scale(1.02)`
                : 'perspective(1000px) rotateX(0deg) rotateY(0deg) scale(1)',
              transition: 'transform 0.2s ease-out',
              color: isHovered ? 'transparent' : '#ffffff',
              background: isHovered 
                ? 'linear-gradient(135deg, #fff 0%, #a78bfa 20%, #f472b6 40%, #60a5fa 60%, #34d399 80%, #fff 100%)'
                : 'none',
              backgroundSize: '400% 400%',
              WebkitBackgroundClip: isHovered ? 'text' : 'unset',
              backgroundClip: isHovered ? 'text' : 'unset',
              animation: isHovered ? 'gradientFlow 4s ease infinite' : 'none',
              textShadow: isHovered ? 'none' : '0 0 80px rgba(255, 255, 255, 0.15)'
            }}
          >
            RHY

            {/* TV Static RGB Layers - Smooth */}
            {isHovered && (
              <>
                {/* Red channel */}
                <span 
                  className="absolute inset-0 pointer-events-none mix-blend-screen"
                  style={{
                    color: 'rgba(255, 80, 80, 0.7)',
                    transform: `translate(${staticOffset.x + mousePos.x * 3}px, ${staticOffset.y}px)`,
                    filter: 'blur(0.5px)',
                    transition: 'color 0.1s ease'
                  }}
                  aria-hidden="true"
                >
                  RHY
                </span>
                {/* Green channel */}
                <span 
                  className="absolute inset-0 pointer-events-none mix-blend-screen"
                  style={{
                    color: 'rgba(80, 255, 120, 0.6)',
                    transform: `translate(${-staticOffset.x * 0.7}px, ${staticOffset.y * 1.2 + mousePos.y * 2}px)`,
                    filter: 'blur(0.3px)',
                    transition: 'color 0.1s ease'
                  }}
                  aria-hidden="true"
                >
                  RHY
                </span>
                {/* Blue channel */}
                <span 
                  className="absolute inset-0 pointer-events-none mix-blend-screen"
                  style={{
                    color: 'rgba(80, 150, 255, 0.7)',
                    transform: `translate(${staticOffset.x * 0.5 - mousePos.x * 2}px, ${-staticOffset.y * 0.8}px)`,
                    filter: 'blur(0.4px)',
                    transition: 'color 0.1s ease'
                  }}
                  aria-hidden="true"
                >
                  RHY
                </span>
                
                {/* Scanline overlay */}
                <span 
                  className="absolute inset-0 pointer-events-none overflow-hidden"
                  style={{
                    background: `repeating-linear-gradient(
                      0deg,
                      transparent,
                      transparent 2px,
                      rgba(0, 0, 0, 0.1) 2px,
                      rgba(0, 0, 0, 0.1) 4px
                    )`,
                    opacity: 0.5,
                    animation: 'scanlines 0.1s linear infinite'
                  }}
                  aria-hidden="true"
                />
              </>
            )}
          </h1>

          {/* Reflection */}
          <div 
            className={`absolute top-full left-0 right-0 h-20 overflow-hidden transition-opacity duration-500 ${isHovered ? 'opacity-15' : 'opacity-10'}`}
            style={{
              transform: 'scaleY(-1)',
              maskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to bottom, rgba(0,0,0,0.4) 0%, transparent 100%)'
            }}
          >
            <span 
              className="hero-title"
              style={{
                filter: 'blur(3px)',
                color: 'rgba(255,255,255,0.3)'
              }}
            >
              RHY
            </span>
          </div>
        </div>

        <p 
          className={`hero-subtitle mt-8 ${isLoaded ? 'animate-fade-in-up delay-300' : 'opacity-0'}`}
          data-testid="hero-subtitle"
        >
          The Next Big Thing
        </p>

        {/* Easter Egg Message Popup - Positioned at TOP of screen */}
        {easterEggMessage && (
          <div className="fixed top-20 left-0 right-0 z-[100] flex items-start justify-center pointer-events-none">
            <div className={`animate-bounce ${easterEggMessage.type === 'birthday' ? 'text-4xl md:text-6xl' : 'text-6xl md:text-8xl'} font-heading font-bold text-center`}
              style={{
                background: easterEggMessage.type === 'birthday' 
                  ? 'linear-gradient(135deg, #ff6b6b, #ffd93d, #6bcb77, #4d96ff, #ff6b6b)'
                  : 'linear-gradient(135deg, #fff, #a78bfa, #f472b6, #fff)',
                backgroundSize: '400% 400%',
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
                animation: 'gradientFlow 2s ease infinite',
                textShadow: '0 0 60px rgba(255,255,255,0.5)'
              }}
            >
              {easterEggMessage.text}
            </div>
          </div>
        )}
      </div>

      {/* Tap Instruction */}
      <div className={`absolute bottom-32 left-1/2 -translate-x-1/2 text-center ${isLoaded ? 'animate-fade-in delay-700' : 'opacity-0'}`}>
        <span className="font-mono text-xs tracking-[0.3em] uppercase text-white/30">
          Tap anywhere to interact
        </span>
      </div>

      {/* Scroll Indicator */}
      <div className="scroll-indicator" data-testid="scroll-indicator">
        <span className="font-mono text-xs tracking-[0.3em] uppercase text-white/40">
          Scroll
        </span>
        <div className="scroll-line" />
      </div>

      <style>{`
        @keyframes gradientFlow {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        
        @keyframes scanlines {
          0% { background-position: 0 0; }
          100% { background-position: 0 4px; }
        }
      `}</style>
    </section>
  );
};

export default Hero;
